/**
 * The DOM half of the joystick, and the only file in the port that knows
 * `navigator.getGamepads` exists. Same split as `keyboard.ts` / `keys.ts` and
 * `pointer.ts`: everything decision-shaped is pure and unit-tested next door
 * (`gamepad-axes.ts`, `gamepad-mapping.ts`, `gamepad-blend.ts`), and this is a
 * poll, two listeners and three fields.
 *
 * ## The Gamepad API is polled, and this polls it once per rendered frame
 *
 * There are no button events. The browser refreshes its `Gamepad` snapshots
 * once per animation-frame turn, so `getGamepads()` returns *the same bytes*
 * however often it is called inside one frame — and the flight loop already
 * samples the keyboard and the mouse exactly once per frame and steps every
 * fixed tick of that frame on the result (`flight-scene.ts`'s `onFrame`, which
 * can run anywhere from zero to eight ticks). Polling per *tick* instead would
 * fabricate sub-frame detail the device never reported, and would break the
 * Mission Recorder's model of one input run per frame. So `frame()` is called
 * from the same line the mouse is, and this file does no timing of its own.
 *
 * ## A browser will not admit to a gamepad until the page has been touched
 *
 * `getGamepads()` returns nothing but nulls until the user has interacted with
 * the document — a privacy measure, and not something a page can opt out of.
 * Nothing here needs to work around it: the poll simply reports no device until
 * the first key or click, `gamepadconnected` fires at that moment, and the
 * CONTROLS panel says so in as many words.
 *
 * ## Hot-plug, and a device vanishing mid-flight
 *
 * Both listeners are registered, but the poll does not depend on them — it
 * re-resolves the device every frame, so a stick that disappears is simply a
 * frame that reports none, and `gamepad-blend.ts` hands the axes back to the
 * keyboard on the next one. The events exist to tell the *panel* (through
 * `onDeviceChange`) that the list has moved, and to drop the held-button set so
 * a button held at the moment the cable came out is not still held when it goes
 * back in.
 */

import { NO_PAD_INPUT, padFrameFrom, stillSuppressed, withoutSuppressed, type PadFrame, type PadSnapshot } from "./gamepad-axes.ts";
import {
  defaultMapping,
  loadBindings,
  mappingFor,
  PAD_COMMANDS,
  rememberMapping,
  saveBindings,
  type GamepadBindings,
  type GamepadMapping,
  type PadAction,
} from "./gamepad-mapping.ts";
import type { SettingsStore } from "../audio/settings.ts";
import type { DiscreteCommand } from "./keys.ts";

export interface GamepadControlsOptions {
  /** Where per-device mappings live; `undefined` in a browser with no storage, which just means nothing persists. */
  readonly store: SettingsStore | undefined;
  /** One-shot actions that are also flight commands (`PAD_COMMANDS`) are dispatched here, exactly as a key press is. */
  readonly onCommand: (command: DiscreteCommand) => void;
  /**
   * True while a full-screen surface owns the input — PREFERENCES, the map
   * sheet, or the calibration panel reading the very device it is measuring.
   * While it is true `frame()` reports nothing at all: no axes, no held
   * buttons, no commands. `snapshot()` keeps working, which is what lets the
   * calibration panel watch a stick it is simultaneously refusing to fly with.
   */
  readonly blocked?: () => boolean;
  /** Called when a device is plugged in or pulled out, so a panel can repaint without polling. */
  readonly onDeviceChange?: () => void;
}

export interface GamepadControls {
  /** One poll; call exactly once per rendered frame. Reports nothing while blocked. */
  frame(): PadFrame;
  /** The raw device reading, live even while blocked. */
  snapshot(): PadSnapshot | undefined;
  /** The mapping in force, or `undefined` when nothing is plugged in. */
  mapping(): GamepadMapping | undefined;
  setMapping(next: GamepadMapping): void;
  dispose(): void;
}

/** `Gamepad` without depending on the DOM lib's exact shape — the four fields this port reads. */
interface PolledGamepad {
  readonly id: string;
  readonly mapping: string;
  readonly axes: readonly number[];
  readonly buttons: readonly { readonly pressed: boolean }[];
  readonly connected: boolean;
}

interface GamepadNavigator {
  getGamepads?: () => readonly (PolledGamepad | null)[];
}

/** The first connected device by index; the API offers no notion of a preferred one. */
function firstConnected(): PolledGamepad | undefined {
  const source = (globalThis.navigator as GamepadNavigator | undefined)?.getGamepads;
  if (typeof source !== "function") return undefined;
  for (const pad of source.call(globalThis.navigator)) {
    if (pad && pad.connected) return pad;
  }
  return undefined;
}

/**
 * Both arrays are **copied**. Firefox hands back the same `Gamepad` object on
 * every `getGamepads()` and mutates it in place, so a snapshot that aliased its
 * arrays would silently be a live view — and anything comparing this frame's
 * reading to the last one would find them identical for ever, which is exactly
 * how a rising edge stops existing.
 */
function snapshotOf(pad: PolledGamepad): PadSnapshot {
  return {
    id: pad.id,
    standard: pad.mapping === "standard",
    axes: [...pad.axes],
    buttons: pad.buttons.map((button) => button.pressed === true),
  };
}

/** Everything one session carries between polls. */
interface PadSession {
  bindings: GamepadBindings;
  /** The connected device's id, or `undefined`; a change is what re-reads the mapping. */
  device: string | undefined;
  mapping: GamepadMapping | undefined;
  /** What was down last poll, which is what makes a rising edge an edge. */
  held: ReadonlySet<PadAction>;
  /** Held over from a blocked stretch; nothing in here acts until it is released. */
  suppressed: ReadonlySet<PadAction>;
}

const NOTHING_HELD: ReadonlySet<PadAction> = new Set<PadAction>();

/** Picks up whatever is plugged in now, loading (or minting) its mapping on the poll the id changes. */
function resolveDevice(session: PadSession): PadSnapshot | undefined {
  const pad = firstConnected();
  if (!pad) {
    session.device = undefined;
    session.mapping = undefined;
    return undefined;
  }
  const snapshot = snapshotOf(pad);
  if (snapshot.id !== session.device) {
    session.device = snapshot.id;
    session.mapping = mappingFor(session.bindings, snapshot.id) ?? defaultMapping(snapshot.id, snapshot.standard, snapshot.axes.length);
    session.held = NOTHING_HELD;
  }
  return snapshot;
}

function forget(session: PadSession): PadFrame {
  session.held = NOTHING_HELD;
  session.suppressed = NOTHING_HELD;
  return NO_PAD_INPUT;
}

/**
 * One poll, and the two halves of what "blocked" has to mean for a control the
 * browser reports as a **level** rather than as events.
 *
 * The held set keeps mirroring the hardware throughout — only the *reporting*
 * is suppressed. Clearing it instead would make a button that was down when the
 * panel opened, and never released, read as a fresh rising edge on the frame the
 * panel shut: a map, a pause and a camera change all arriving at once from a
 * press meant for the panel.
 *
 * And whatever is still down as the block lifts stays suppressed until it is let
 * go (`stillSuppressed`), which is what the keyboard gets for free from the
 * overlay's capture-phase listener. Without it the trigger a player just used to
 * confirm the last calibration step opens fire on the way out — measured, in a
 * headed run, as 109 rounds.
 */
function pollFrame(session: PadSession, options: GamepadControlsOptions): PadFrame {
  const snapshot = resolveDevice(session);
  if (!snapshot || !session.mapping) return forget(session);
  const padFrame = padFrameFrom(snapshot, session.mapping, session.held);
  session.held = padFrame.held;
  if (options.blocked?.() === true) {
    session.suppressed = padFrame.held;
    return NO_PAD_INPUT;
  }
  session.suppressed = stillSuppressed(session.suppressed, padFrame.held);
  const live = withoutSuppressed(padFrame, session.suppressed);
  for (const action of live.commands) {
    const command = PAD_COMMANDS[action];
    if (command) options.onCommand(command);
  }
  return live;
}

export function createGamepadControls(options: GamepadControlsOptions): GamepadControls {
  const session: PadSession = {
    bindings: loadBindings(options.store),
    device: undefined,
    mapping: undefined,
    held: NOTHING_HELD,
    suppressed: NOTHING_HELD,
  };

  function onConnectionChange(): void {
    // Force the next poll to re-read the mapping even if the browser hands back
    // a device with the same id, and drop a button that was down when the plug
    // came out — it is not down now, and nothing will ever say so.
    session.device = undefined;
    forget(session);
    options.onDeviceChange?.();
  }

  window.addEventListener("gamepadconnected", onConnectionChange);
  window.addEventListener("gamepaddisconnected", onConnectionChange);

  return {
    frame: () => pollFrame(session, options),
    snapshot: () => resolveDevice(session),
    mapping: () => session.mapping,
    setMapping(next) {
      session.mapping = next;
      session.device = next.deviceId;
      session.bindings = rememberMapping(session.bindings, next);
      saveBindings(options.store, session.bindings);
    },
    dispose() {
      window.removeEventListener("gamepadconnected", onConnectionChange);
      window.removeEventListener("gamepaddisconnected", onConnectionChange);
      session.device = undefined;
      session.mapping = undefined;
      forget(session);
    },
  };
}
